import type { Next } from '@zevik/schemas';

/**
 * When the page re-reads the snapshot, and how often
 * (docs/DECISIONS.md ZD-124 §4).
 *
 * ## Only while `running`
 *
 * A person halt is stable until a person acts; `ready` and `stopped` are
 * stable until somebody presses. Polling any of them would read the same
 * answer for ever, and — the constraint that matters — a page that showed a
 * run progressing has to be reading that off `next` and not off its own
 * timer. So the page polls exactly while `next.kind === 'running'`, and the
 * moment the answer changes kind the polling stops with it.
 *
 * ## The interval
 *
 * Two seconds. Measured against the live run (ZD-123 §11): the shortest stage
 * was 1.3 s and the longest 8.2 s, so a tick shorter than the shortest stage
 * would mostly show the same stage twice, and a tick longer than a couple of
 * seconds leaves a person looking at a stage that has already finished.
 * Each tick is one Durable Object request — a snapshot plus a `getAlarm` —
 * so the second leg of the live run costs about nine reads. The provider
 * regime ZD-121 §2 describes (24–195 s per document once a model is wired)
 * will want a longer interval and a backoff; that is a change to this
 * constant and its comment, in one place, and nothing else.
 */

export const POLL_INTERVAL_MS = 2_000;

export function shouldPoll(next: Next | null): boolean {
  return next?.kind === 'running';
}
